import { isSupportedLocale, resolveLocale, } from "./locale";
/**
 * The memory adapter. Handing the SAME instance to a second store is how a
 * test spells "the user closed the app and opened it again".
 */
export function createMemoryLocalePersistence(initial = null) {
    let stored = initial;
    return {
        read: () => Promise.resolve(isSupportedLocale(stored) ? stored : null),
        write: (locale) => {
            stored = locale;
            return Promise.resolve();
        },
        clear: () => {
            stored = null;
            return Promise.resolve();
        },
    };
}
/**
 * Where the field lives, and in what vocabulary. Both are what the Expo app
 * wrote to its native key-value store (`storage/locale-preference-store.ts`
 * in the `expo-app-final` tag): the key `mypocketgym.locale`, and the value a
 * `SupportedLocale` tag verbatim — `"en-US"` or `"pt-BR"`. Kept identical so
 * that migrating an existing install is a copy, not a translation.
 */
export const LOCALE_STORAGE_KEY = "mypocketgym.locale";
/**
 * The real adapter behind {@link LocalePersistence}, over any key-value store.
 *
 * The value is validated on the way OUT rather than trusted. What is on disk
 * was written by whatever build was installed at the time — `"en"` from before
 * the tags carried their region, or a half-written entry — and a value that is
 * not in `SUPPORTED_LOCALES` reads as ABSENT, which makes the store seed it
 * again from the device. Passing it through would index the message catalogue
 * with a key it does not have.
 */
export function createKeyValueLocalePersistence(storage, key = LOCALE_STORAGE_KEY) {
    return {
        read: async () => {
            const stored = await storage.getItem(key);
            return isSupportedLocale(stored) ? stored : null;
        },
        write: async (locale) => {
            await storage.setItem(key, locale);
        },
        clear: async () => {
            await storage.removeItem(key);
        },
    };
}
export function createLocaleStore(initial = null, options = {}) {
    const persistence = options.persistence ?? createMemoryLocalePersistence();
    let current = normalize(initial);
    let hydration = null;
    const listeners = new Set();
    const notify = () => {
        for (const listener of listeners) {
            listener();
        }
    };
    const assign = (next) => {
        // Notifying on a no-op set would re-render every subscribed screen for
        // nothing, and `useSyncExternalStore` compares snapshots by identity —
        // so an unchanged value here is genuinely free only if we stay quiet.
        if (next === current) {
            return false;
        }
        current = next;
        notify();
        return true;
    };
    const persist = (next) => {
        const written = next === null ? persistence.clear() : persistence.write(next);
        written.catch((error) => {
            options.onPersistError?.(error);
        });
    };
    return {
        getSnapshot: () => current,
        hydrate: (devicePreferences) => {
            hydration ??= (async () => {
                // A read that throws is a device whose storage is unavailable. It
                // means the same thing to us as an empty one — seed from the device —
                // so `hydrate` resolves either way and callers have no error path.
                const stored = await persistence.read().catch((error) => {
                    options.onPersistError?.(error);
                    return null;
                });
                if (stored !== null) {
                    assign(stored);
                    return stored;
                }
                // The user chose a language while the read was in flight. Their choice
                // is already persisted and is newer than anything on disk.
                if (current !== null) {
                    return current;
                }
                const seeded = resolveLocale(devicePreferences);
                assign(seeded);
                persist(seeded);
                return seeded;
            })();
            return hydration;
        },
        set: (locale) => {
            const next = normalize(locale);
            if (!assign(next)) {
                return;
            }
            if (next === null) {
                // Cleared on purpose: the field is gone, so the next `hydrate` is
                // allowed to seed it from the device again.
                hydration = null;
            }
            else {
                // An explicit choice is the answer hydration would otherwise seed, so
                // it closes that window too: a read landing later must not overwrite
                // what the user just picked.
                hydration ??= Promise.resolve(next);
            }
            persist(next);
        },
        subscribe: (listener) => {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
    };
}
/**
 * A persisted or hand-entered value may be anything — a locale tag from a
 * build that spelled English `en`, a typo in a dev setting. Anything
 * unrecognised means "not set", so the device seeds it again, rather than
 * reaching the catalogue as a key it does not have.
 */
function normalize(locale) {
    return isSupportedLocale(locale) ? locale : null;
}
//# sourceMappingURL=locale-store.js.map